import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Globe, Calculator, DollarSign, ArrowRight, BarChart3,
  Search, Map as MapIcon, TrendingDown, ShieldCheck, FileText, Flag,
} from 'lucide-react';
// NOTE: Map is aliased to MapIcon to avoid shadowing the native JavaScript
// `Map` constructor used below (e.g. `new Map(...)`). Without the alias,
// minified bundles render the icon component when code tries to instantiate
// a Map and crash with "X is not a constructor".
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { loadStaticData } from '@/lib/offlineData';
import { getCountrySlug } from '@/lib/countryUtils';

// ─── Types ──────────────────────────────────────────────────────────────────

interface CountryInfo {
  country_code: string;
  country_name: string;
  flag_emoji: string;
  healthcare_system: string;
  currency_code: string;
  default_coverage: string;
}

interface OverallComparison {
  country_code: string;
  scenario_count: number;
  avg_intl_cost: number;
  avg_us_national_cost: number;
  us_vs_intl_multiplier: number;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatUSD(amount: number): string {
  if (!amount || !isFinite(amount)) return '$0';
  if (amount >= 10000) return `$${(amount / 1000).toFixed(0)}k`;
  if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}k`;
  return `$${Math.round(amount).toLocaleString()}`;
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function InternationalHub() {
  const brand = useBrand();
  const { t } = useTranslation(['international', 'common']);
  const isAhp = brand.key === 'ahp';
  const { isDark } = useTheme();
  const dark = isAhp && isDark;

  // ── Site color system (matches CountryDetail / hospital profile) ─────────
  const pageBg = isAhp ? (dark ? '#0a1628' : '#FAF9F6') : undefined;
  const cardBg = isAhp ? (dark ? '#0f1d2f' : '#ffffff') : undefined;
  const borderColor = isAhp ? (dark ? 'rgba(197,165,90,0.2)' : 'rgba(0,0,0,0.1)') : undefined;
  const textColor = isAhp ? (dark ? '#FAF9F6' : '#1B1B1B') : undefined;
  const mutedText = isAhp ? (dark ? 'rgba(250,249,246,0.6)' : 'rgba(27,27,27,0.62)') : undefined;
  const gold = '#C5A55A';
  const navy = dark ? '#7da6d4' : '#002868';
  const red = dark ? '#e85d6c' : '#B22234';
  const green = dark ? '#5fbf8f' : '#0F8A5F';
  const serif = "'Instrument Serif', Georgia, serif";

  const sectionCardStyle: React.CSSProperties = {
    background: cardBg,
    border: `1px solid ${borderColor}`,
    borderRadius: 14,
    overflow: 'hidden',
    boxShadow: dark ? '0 2px 16px rgba(0,0,0,0.4)' : '0 2px 16px rgba(0,0,0,0.06)',
  };
  const sectionHeadingStyle: React.CSSProperties = {
    color: textColor,
    fontFamily: serif,
    fontWeight: 400,
    fontSize: '1.35rem',
  };
  const eyebrowStyle: React.CSSProperties = {
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.14em',
    fontWeight: 700,
    color: gold,
  };

  // ── Data ─────────────────────────────────────────────────────────────────
  // First paint uses the 13KB overview (countries + overallComparison) — the
  // 46MB international_stats.json is fetched lazily ONLY for the heatmap
  // preview rows further down the page.
  const { data: overview } = useQuery({
    queryKey: ['international-hub-overview'],
    queryFn: () => loadStaticData('international_overview.json'),
    staleTime: 1000 * 60 * 60,
  });
  const { data: bigStats } = useQuery({
    queryKey: ['international-hub-heatmap-data'],
    queryFn: () => loadStaticData('international_stats.json'),
    staleTime: 1000 * 60 * 60,
    enabled: !!overview, // start after first paint content is in
  });

  const countries = useMemo(() => {
    if (!overview) return [];
    return (((overview as any).countries as CountryInfo[]) || []).slice().sort((a, b) =>
      a.country_name.localeCompare(b.country_name)
    );
  }, [overview]);

  const overall = useMemo(() => {
    if (!overview) return [];
    return (((overview as any).overallComparison as OverallComparison[]) || [])
      .filter(c => c.us_vs_intl_multiplier > 0 && isFinite(c.us_vs_intl_multiplier))
      .sort((a, b) => b.us_vs_intl_multiplier - a.us_vs_intl_multiplier);
  }, [overview]);

  const countryMap = useMemo(() => {
    const map: Record<string, CountryInfo> = {};
    countries.forEach(c => { map[c.country_code] = c; });
    return map;
  }, [countries]);

  const countryCount = countries.length;
  const totalScenarioComparisons = useMemo(
    () => overall.reduce((a, c) => a + (c.scenario_count || 0), 0), [overall]);
  const medianMultiplier = useMemo(() => {
    if (!overall.length) return 0;
    const sorted = overall.map(o => o.us_vs_intl_multiplier).sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)];
  }, [overall]);

  // multiplier tone: bigger = bigger US overcharge = bigger savings abroad
  const multHex = (m: number) => (m >= 15 ? green : m >= 6 ? gold : red);

  // JSON-LD structured data for SEO
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: t('meta.hubTitle', { count: countryCount, brandName: brand.name }),
    description: t('meta.hubDescription', { count: countryCount }),
    url: `${brand.primaryDomain}/international`,
    publisher: { '@type': 'Organization', name: brand.name, url: brand.primaryDomain },
    about: {
      '@type': 'Thing',
      name: 'International Healthcare Price Comparison',
      description: `Comparing healthcare prices across ${countryCount} countries using official government fee schedules`,
    },
  };

  const statChip = (label: string, value: string, accent: string) => (
    <div key={label} className="rounded-xl px-4 py-3 text-center" style={{ ...sectionCardStyle, borderLeft: `3px solid ${accent}` }}>
      <div style={{ ...eyebrowStyle, color: mutedText }}>{label}</div>
      <div className="tabular-nums" style={{ fontFamily: serif, fontWeight: 400, fontSize: '1.6rem', color: accent }}>{value}</div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: pageBg }}>
      <Helmet>
        <title>{t('meta.hubTitle', { count: countryCount, brandName: brand.name })}</title>
        <meta name="description" content={t('meta.hubDescription', { count: countryCount })} />
        <meta property="og:title" content={t('meta.hubTitle', { count: countryCount, brandName: brand.name })} />
        <meta property="og:description" content={t('meta.hubDescription', { count: countryCount })} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${brand.primaryDomain}/international`} />
        <link rel="canonical" href={`${brand.primaryDomain}/international`} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* ─── Overview / Hero ───────────────────────────────────────────── */}
      <section id="intl-overview" className="scroll-mt-24 border-b" style={{ borderColor }}>
        <div className="max-w-6xl mx-auto px-4 pt-12 pb-10 sm:pt-16 sm:pb-12">
          <div style={eyebrowStyle} className="mb-3 flex items-center gap-2">
            <Globe className="w-3.5 h-3.5" />
            {t('hub.hero.badge', { count: countryCount, defaultValue: `${countryCount} countries · real government fee schedules` })}
          </div>
          <h1 style={{ color: textColor, fontFamily: serif, fontWeight: 400, letterSpacing: '-0.5px' }}
              className="text-4xl sm:text-5xl leading-tight mb-4">
            {t('hub.hero.title', { defaultValue: 'Healthcare Prices' })}{' '}
            <span style={{ color: navy }}>{t('hub.hero.titleHighlight', { defaultValue: 'Around the World' })}</span>
          </h1>
          <p className="text-base sm:text-lg max-w-3xl mb-8" style={{ color: mutedText, lineHeight: 1.6 }}>
            {t('hub.hero.subtitle', { count: countryCount })}
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl">
            {statChip(t('hub.stats.countries', { defaultValue: 'Countries' }), String(countryCount || '—'), navy)}
            {statChip('Scenario comparisons', totalScenarioComparisons ? totalScenarioComparisons.toLocaleString() : '—', gold)}
            {statChip('Median US multiple', medianMultiplier ? `${medianMultiplier.toFixed(0)}x` : '—', red)}
            {statChip('Source', 'Gov. tariffs', green)}
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 py-10 space-y-14">

        {/* ─── Savings leaderboard ──────────────────────────────────────── */}
        <section id="intl-savings" className="scroll-mt-24">
          <div className="flex items-center gap-2.5 mb-1">
            <TrendingDown className="w-4 h-4" style={{ color: gold }} />
            <span style={eyebrowStyle}>{t('hub.sections.topSavings.title', { defaultValue: 'Biggest savings vs US' })}</span>
          </div>
          <h2 style={sectionHeadingStyle} className="mb-5">
            {t('hub.sections.topSavings.subtitle', { defaultValue: 'Where you save the most on common procedures' })}
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {overall.slice(0, 12).map((item) => {
              const country = countryMap[item.country_code];
              if (!country) return null;
              const savings = Math.round(((item.avg_us_national_cost - item.avg_intl_cost) / item.avg_us_national_cost) * 100);
              const slug = getCountrySlug(item.country_code);
              if (!slug) return null;

              return (
                <Link key={item.country_code} to={`/international/${slug}`} className="block group">
                  <div style={sectionCardStyle} className="p-4 h-full transition-transform group-hover:-translate-y-0.5">
                    <div className="flex items-center gap-3 mb-2.5">
                      <span className="text-3xl">{country.flag_emoji}</span>
                      <div className="flex-1 min-w-0">
                        <p className="truncate" style={{ color: textColor, fontFamily: serif, fontSize: '1.05rem' }}>{country.country_name}</p>
                        <p className="text-xs truncate" style={{ color: mutedText }}>{country.healthcare_system}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="tabular-nums" style={{ color: multHex(item.us_vs_intl_multiplier), fontFamily: serif, fontSize: '1.5rem' }}>
                          {item.us_vs_intl_multiplier.toFixed(0)}x
                        </p>
                        <p style={{ ...eyebrowStyle, color: mutedText, fontSize: '0.58rem' }}>US charges</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs" style={{ color: mutedText }}>
                      <span>{item.scenario_count} scenarios · avg {formatUSD(item.avg_intl_cost)} vs {formatUSD(item.avg_us_national_cost)}</span>
                      {savings > 0 && (
                        <span className="px-2 py-0.5 rounded-full font-semibold" style={{ color: green, border: `1px solid ${green}55` }}>
                          −{savings}%
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* ─── Calculator CTA ───────────────────────────────────────────── */}
        <section>
          <div style={sectionCardStyle} className="p-6 sm:p-8">
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
              <div className="flex-1">
                <div style={eyebrowStyle} className="mb-2 flex items-center gap-2">
                  <Calculator className="w-3.5 h-3.5" /> {t('hub.sections.calculator.title', { defaultValue: 'Cost calculator' })}
                </div>
                <h2 style={sectionHeadingStyle} className="mb-2">{t('hub.sections.calculator.subtitle', { defaultValue: 'What would your procedure cost abroad?' })}</h2>
                <Link to="/international/calculator"
                  className="inline-flex items-center gap-2 mt-2 px-4 py-2 rounded-lg text-sm font-semibold text-white"
                  style={{ background: red }}>
                  {t('hub.sections.calculator.cta', { defaultValue: 'Open Calculator' })} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid grid-cols-3 gap-2.5 w-full max-w-sm">
                {[['🇺🇸', '$52,000', 'US Average', red], ['🇪🇸', '$7,200', 'Spain', green], ['🇹🇭', '$3,400', 'Thailand', green]].map(([flag, price, label, tone], i) => (
                  <div key={i} className="p-3 rounded-lg text-center" style={{ border: `1px solid ${String(tone)}44`, background: dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
                    <p className="text-lg">{flag}</p>
                    <p className="tabular-nums font-semibold text-sm" style={{ color: String(tone), fontFamily: serif, fontSize: '1.05rem' }}>{price}</p>
                    <p className="text-[10px] mt-0.5" style={{ color: mutedText }}>{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ─── $100 challenge CTA ───────────────────────────────────────── */}
        <section>
          <div style={sectionCardStyle} className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div className="flex-1">
                <div style={eyebrowStyle} className="mb-2 flex items-center gap-2">
                  <DollarSign className="w-3.5 h-3.5" /> {t('hub.sections.challenge.title', { defaultValue: 'The $100 challenge' })}
                </div>
                <h2 style={sectionHeadingStyle} className="mb-2">{t('hub.sections.challenge.subtitle', { defaultValue: 'How much healthcare does $100 buy in each country?' })}</h2>
                <Link to="/international/100-dollar-challenge"
                  className="inline-flex items-center gap-2 mt-2 px-4 py-2 rounded-lg text-sm font-semibold"
                  style={{ color: navy, border: `1.5px solid ${navy}` }}>
                  {t('hub.sections.challenge.cta', { defaultValue: 'Take the tour' })} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="text-center sm:text-right shrink-0">
                <p style={{ color: gold, fontFamily: serif, fontSize: '3.2rem', lineHeight: 1 }}>$100</p>
                <p className="text-xs mt-1" style={{ color: mutedText }}>vs {countryCount} countries</p>
              </div>
            </div>
          </div>
        </section>

        {/* ─── Browse by country ────────────────────────────────────────── */}
        <section id="intl-countries" className="scroll-mt-24">
          <div className="flex items-center gap-2.5 mb-1">
            <Flag className="w-4 h-4" style={{ color: gold }} />
            <span style={eyebrowStyle}>{t('hub.sections.countries.title', { defaultValue: 'Browse by country' })}</span>
          </div>
          <h2 style={sectionHeadingStyle} className="mb-5">
            {t('hub.sections.countries.subtitle', { defaultValue: 'Every country, every published tariff' })}
          </h2>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2.5">
            {countries.map(country => {
              const stats = overall.find(o => o.country_code === country.country_code);
              const slug = getCountrySlug(country.country_code);
              // The US row is the 1x baseline — no country page exists, so a
              // Link would point at /international/undefined (bot-404).
              if (!slug) {
                return (
                  <div key={country.country_code} style={sectionCardStyle} className="p-3 text-center h-full opacity-80">
                    <span className="text-3xl">{country.flag_emoji}</span>
                    <p className="text-sm mt-1 truncate" style={{ color: textColor, fontFamily: serif }}>{country.country_name}</p>
                    <p className="text-xs font-semibold mt-0.5" style={{ color: mutedText }}>{"baseline (1×)"}</p>
                  </div>
                );
              }

              return (
                <Link key={country.country_code} to={`/international/${slug}`} className="block group">
                  <div style={sectionCardStyle} className="p-3 text-center h-full transition-transform group-hover:-translate-y-0.5">
                    <span className="text-3xl">{country.flag_emoji}</span>
                    <p className="text-sm mt-1 truncate" style={{ color: textColor, fontFamily: serif }}>{country.country_name}</p>
                    {stats && (
                      <p className="text-xs font-semibold mt-0.5 tabular-nums" style={{ color: multHex(stats.us_vs_intl_multiplier) }}>
                        {stats.us_vs_intl_multiplier.toFixed(0)}x cheaper
                      </p>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* ─── Heatmap preview (lazy 46MB dataset) ─────────────────────── */}
        <section>
          <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2.5 mb-1">
                <BarChart3 className="w-4 h-4" style={{ color: gold }} />
                <span style={eyebrowStyle}>Price heatmap</span>
              </div>
              <h2 style={sectionHeadingStyle}>Price ratios at a glance — procedures vs countries</h2>
            </div>
            <Link to="/international/heatmap" className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold shrink-0"
              style={{ color: navy, border: `1.5px solid ${navy}` }}>
              View Full Heatmap <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div style={sectionCardStyle} className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr style={{ background: dark ? 'rgba(0,20,60,0.8)' : 'rgba(0,40,104,0.06)' }}>
                  <th className="text-left p-2.5 pl-3 font-semibold min-w-[160px]" style={{ color: textColor }}>Procedure</th>
                  <th className="p-2.5 text-right font-semibold min-w-[65px]" style={{ color: red }}>🇺🇸 US</th>
                  {overall.slice(0, 8).map(item => {
                    const c = countryMap[item.country_code];
                    return c ? (
                      <th key={item.country_code} className="p-1.5 text-center min-w-[50px]">
                        <span className="text-base">{c.flag_emoji}</span>
                      </th>
                    ) : null;
                  })}
                </tr>
              </thead>
              <tbody>
                {[
                  { code: '27447', label: 'Knee Replacement' },
                  { code: '99213', label: 'Office Visit' },
                  { code: '70553', label: 'Brain MRI' },
                  { code: '45380', label: 'Colonoscopy' },
                  { code: '93000', label: 'ECG/EKG' },
                ].map((proc, idx) => {
                  const usP = ((bigStats as any)?.usPricing as any[] || []).find((p: any) => p.cpt_code === proc.code)?.avg_gross_charge || 0;
                  const priceList = ((bigStats as any)?.procedurePrices as any[] || []).filter((p: any) => p.us_code === proc.code);
                  const priceMap = new Map(priceList.map((p: any) => [p.country_code, p.price_usd]));

                  return (
                    <tr key={proc.code} style={idx % 2 === 0 ? undefined : { background: dark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                      <td className="p-2.5 pl-3" style={{ color: textColor }}>
                        <span className="font-mono mr-1.5" style={{ color: gold }}>{proc.code}</span>
                        <span>{proc.label}</span>
                      </td>
                      <td className="p-2.5 text-right font-medium tabular-nums" style={{ color: red }}>
                        {usP > 0 ? (usP >= 1000 ? `$${(usP / 1000).toFixed(0)}k` : `$${usP.toFixed(0)}`) : '…'}
                      </td>
                      {overall.slice(0, 8).map(item => {
                        const intlP = priceMap.get(item.country_code);
                        if (!intlP || intlP <= 0 || usP <= 0) return <td key={item.country_code} className="p-1 text-center"><div className="h-7 rounded" style={{ background: dark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }} /></td>;
                        const ratio = usP / intlP;
                        const bg = ratio >= 10 ? green : ratio >= 3 ? gold : red;
                        return (
                          <td key={item.country_code} className="p-1 text-center">
                            <div className="h-7 rounded flex items-center justify-center font-semibold text-white tabular-nums" style={{ background: bg }}>
                              {ratio >= 10 ? `${ratio.toFixed(0)}x` : `${ratio.toFixed(1)}x`}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* ─── Code explorer CTA ────────────────────────────────────────── */}
        <section>
          <div style={sectionCardStyle} className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div className="flex-1">
                <div style={eyebrowStyle} className="mb-2 flex items-center gap-2">
                  <Search className="w-3.5 h-3.5" /> International code explorer
                </div>
                <h2 style={sectionHeadingStyle} className="mb-2">
                  Search any CPT code and see how it's coded in {Math.max(countryCount - 1, 0)} foreign systems
                </h2>
                <Link to="/international/code-explorer"
                  className="inline-flex items-center gap-2 mt-2 px-4 py-2 rounded-lg text-sm font-semibold"
                  style={{ color: navy, border: `1.5px solid ${navy}` }}>
                  Explore Codes <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="hidden lg:flex flex-col gap-1 text-sm font-mono" style={{ color: mutedText }}>
                <span>99213 → <span style={{ color: gold }}>WF01B</span> 🇬🇧</span>
                <span>27447 → <span style={{ color: gold }}>NFKA008</span> 🇫🇷</span>
                <span>43239 → <span style={{ color: gold }}>E7611</span> 🇰🇷</span>
              </div>
            </div>
          </div>
        </section>

        {/* ─── Methodology ──────────────────────────────────────────────── */}
        <section className="border-t pt-10" style={{ borderColor }}>
          <div className="flex items-center gap-2.5 mb-1">
            <ShieldCheck className="w-4 h-4" style={{ color: gold }} />
            <span style={eyebrowStyle}>{t('hub.sections.methodology.title', { defaultValue: 'Methodology' })}</span>
          </div>
          <h2 style={sectionHeadingStyle} className="mb-6">
            {t('hub.sections.methodology.subtitle', { defaultValue: 'Official sources, transparent mapping' })}
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: FileText, text: t('hub.sections.methodology.point1') },
              { icon: BarChart3, text: t('hub.sections.methodology.point2', { codeCount: '442K' }) },
              { icon: ShieldCheck, text: t('hub.sections.methodology.point3') },
              { icon: Globe, text: t('hub.sections.methodology.point4') },
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <div className="shrink-0 p-2 rounded-lg" style={{ background: `${gold}18`, border: `1px solid ${gold}33` }}>
                  <item.icon className="w-4.5 h-4.5 w-5 h-5" style={{ color: gold }} />
                </div>
                <p className="text-sm" style={{ color: mutedText, lineHeight: 1.55 }}>{item.text}</p>
              </div>
            ))}
          </div>

          <p className="text-xs text-center mt-8" style={{ color: mutedText }}>
            {t('common.dataDisclaimer', { count: countryCount, ns: 'international' })}
          </p>
        </section>
      </div>
    </div>
  );
}
